import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthActions, useAuthUser } from "@/context/auth/AuthContext";
import {
  useShowData,
  useShowDataActions,
} from "@/context/show-data/ShowDataContext";
import usePermissions from "@/hooks/usePermissions";
import {
  ChevronDown,
  CreditCard,
  Eye,
  EyeClosed,
  Home,
  Lightbulb,
  LogOut,
  Menu,
  Moon,
  RotateCw,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { checkIfHasPermission } from "../sidebar/utils/permissionUtils";
import {
  SidebarItemWithItems,
  sidebarLinks,
} from "../sidebar/utils/sidebarLinks";
import CollapsibleItem from "./collapsible-item/CollapsibleItem";
import Item from "./item/Item";
import Notifications from "./notifications/Notifications";
import { useSocket } from "@/hooks/useSocket";
import OpenCashdesk from "./open-cashdesk/OpenCashdesk";
import IndicadorDeLaCaja from "./caja/IndicadorDeLaCaja";
import SearchInput from "./search-input/SearchInput";
import { Image } from "@unpic/react";
import TrudLogo from "@/assets/images/logos/isologo.webp";

interface TopbarProps {
  noSidebar?: boolean;
}

function Topbar({ noSidebar }: TopbarProps) {
  // Una sola conexión a `/messaging/ws` por sesión. Vive acá y no dentro de
  // `<Notifications />` porque abajo hay DOS —desktop y mobile—: CSS oculta una,
  // pero React monta las dos y cada una abría su propio socket. Ver [BUG-42].
  const { status: socketStatus } = useSocket();

  const [topBarWidth, setTopBarWidth] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const topBar = useRef(null);
  const { toggleShowData } = useShowDataActions();
  const showData = useShowData();
  const user = useAuthUser();
  const { logout } = useAuthActions();
  const { setTheme, theme } = useTheme();
  const { canRead: canReadCaja } = usePermissions("/tesoreria/caja");
  const navigate = useNavigate();
  const isOwner = user?.type === "owner";

  const resize_ob =
    typeof window !== "undefined" &&
    typeof ResizeObserver !== "undefined" &&
    new ResizeObserver(function (entries) {
      // since we are observing only a single element, so we access the first element in entries array
      const rect = entries[0].contentRect;

      // current width & height
      const width = rect.width;
      // let height = rect.height;

      setTopBarWidth(width);
    });

  useEffect(() => {
    if (topBar.current && resize_ob) {
      resize_ob.observe(topBar.current);
    }

    const refCurrent = topBar.current;

    return () => {
      if (refCurrent && resize_ob) {
        resize_ob.unobserve(refCurrent);
      }
    };
  }, [topBar]);

  const filteredSidebarLinks =
    user?.type === "owner"
      ? sidebarLinks
      : sidebarLinks
          .filter((item) => {
            if (item.permissionLink === "free") {
              return true;
            }

            if (item.permissionLink) {
              return checkIfHasPermission(user, item.permissionLink, "read");
            } else {
              return true;
            }
          })
          .map((item) => {
            if (item.items) {
              return {
                ...item,
                items: item.items.filter((subItem) => {
                  if (subItem.permissionLink) {
                    return checkIfHasPermission(
                      user,
                      subItem.permissionLink,
                      "read"
                    );
                  } else {
                    return true;
                  }
                }),
              };
            }
            return item;
          });

  return (
    <div className="text-text-primary sticky top-0 z-10 bg-background-100 transition-all duration-300">
      <div
        ref={topBar}
        className={`grid grid-cols-[auto_1fr_auto] gap-5 h-[55px] flex items-center justify-between px-[15px] transition-all duration-300`}
      >
        <div className="flex flex-wrap items-baseline transition-all duration-300 flex-col md:flex-row md:gap-2 sm:mt-0 xs:items-start xs:gap-[5px]">
          <div className="flex items-center gap-2">
            {noSidebar && (
              <Home
                className="icon text-text-secondary hover:text-text-primary transition cursor-pointer"
                onClick={() => navigate("/")}
              />
            )}

            <h2 className="text-[20px] font-extrabold text-text-primary transition-all duration-300 whitespace-nowrap overflow-hidden text-ellipsis lg:max-w-[175px]">
              {user?.businessName || "Tienda"}
            </h2>
          </div>
          <p className="text-[14px] font-medium text-text-secondary transition-all duration-300">
            Panel de Control
          </p>
        </div>

        <div></div>

        <div className={`items-center gap-4 hidden md:flex`}>
          {/* La caja offline: sólo si el equipo es una caja (#344) */}
          <IndicadorDeLaCaja />

          {/* Open cashdesk */}
          {canReadCaja && <OpenCashdesk />}

          {/* Reload icon */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger onClick={() => navigate(0)}>
                <RotateCw className="icon hover:text-brand-primary transition" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Recargar página</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Buscador */}
          {topBarWidth > 800 && <SearchInput />}

          {/* Icono ojo */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger onClick={toggleShowData}>
                {showData ? (
                  <Eye className="icon hover:text-brand-primary transition" />
                ) : (
                  <EyeClosed className="icon hover:text-brand-primary transition" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{showData ? "Ocultar valores" : "Mostrar valores"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Icono luna */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger
                onClick={() => {
                  setTheme(theme === "dark" ? "light" : "dark");
                }}
              >
                {theme === "dark" ? (
                  <Lightbulb className="icon hover:text-brand-primary transition" />
                ) : (
                  <Moon className="icon hover:text-brand-primary transition" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{theme === "dark" ? "Modo claro" : "Modo oscuro"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Notifications */}
          <Notifications socketStatus={socketStatus} />

          {/* User avatar and name */}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <div className="cursor-pointer flex items-center gap-2">
                <Avatar className="">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback>
                    {user?.name?.[0]?.toUpperCase()}
                    {user?.lastName?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <p className="transition underline decoration-transparent decoration-[1px] hover:underline hover:decoration-brand-primary hover:decoration-[3px]">
                  Hola, <span className="font-semibold">{user?.name}</span>
                </p>

                <ChevronDown
                  className={`w-3 h-3 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent className="mx-4 p-0 w-44">
              <div className="py-1">
                {isOwner && (
                  <div
                    className="flex px-3 py-2 items-center gap-2 cursor-pointer hover:bg-background-200 transition"
                    onClick={() => {
                      setIsOpen(false);
                      navigate("/mi-suscripcion");
                    }}
                  >
                    <CreditCard className="h-4 w-4" />
                    <p className="text-sm">Mi plan</p>
                  </div>
                )}
                <div
                  className="flex px-3 py-2 items-center gap-2 cursor-pointer hover:bg-background-200 transition"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  <p className="text-sm">Salir</p>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile menu */}
        <div className="flex md:hidden items-center gap-4">
          {/* Notifications */}
          <Notifications socketStatus={socketStatus} />

          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Menu className="text-text-primary cursor-pointer flex text-2xl hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" />
            </SheetTrigger>
            <SheetContent className="flex flex-col items-start justify-start pb-0 px-0">
              <SheetHeader className="px-4">
                <Image
                  src={TrudLogo}
                  alt="Trud"
                  width={120}
                  height={40}
                  priority
                  className={"cursor-pointer"}
                />
              </SheetHeader>

              <div
                className={`w-full grid grid-cols-3 mx-auto items-center justify-center place-items-center py-4 px-4`}
              >
                {/* Reload icon */}
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger onClick={() => navigate(0)}>
                      <RotateCw className="icon hover:text-brand-primary transition" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Recargar página</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Buscador */}
                {topBarWidth > 800 && <SearchInput />}

                {/* Icono ojo */}
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger onClick={toggleShowData}>
                      {showData ? (
                        <Eye className="icon hover:text-brand-primary transition" />
                      ) : (
                        <EyeClosed className="icon hover:text-brand-primary transition" />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{showData ? "Ocultar valores" : "Mostrar valores"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Icono luna */}
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger
                      onClick={() => {
                        setTheme(theme === "dark" ? "light" : "dark");
                      }}
                    >
                      {theme === "dark" ? (
                        <Lightbulb className="icon hover:text-brand-primary transition" />
                      ) : (
                        <Moon className="icon hover:text-brand-primary transition" />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{theme === "dark" ? "Modo claro" : "Modo oscuro"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="w-full flex-1 px-4">
                {filteredSidebarLinks.map((item) => {
                  if (item.items && item.items.length > 0) {
                    return (
                      <CollapsibleItem
                        item={item as SidebarItemWithItems}
                        key={item.id}
                        open={sheetOpen}
                      />
                    );
                  } else {
                    return <Item item={item} key={item.id} open={sheetOpen} />;
                  }
                })}
              </div>

              <SheetFooter className="sticky bottom-0 bg-gradient w-full flex-col">
                {isOwner && (
                  <div
                    className="flex p-4 items-center gap-2 cursor-pointer text-white hover:bg-white/10 transition"
                    onClick={() => {
                      setSheetOpen(false);
                      navigate("/mi-suscripcion");
                    }}
                  >
                    <CreditCard className="icon" />
                    <p>Mi plan</p>
                  </div>
                )}
                <div
                  className="flex p-4 items-center gap-2 cursor-pointer text-white hover:bg-white/10 transition"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                >
                  <LogOut className="icon" />
                  <p>Salir</p>
                </div>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}

export default Topbar;
